import { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  Download, Database, Filter, Search, TableProperties,
  ChevronRight, Columns, CheckSquare, Square, X
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getAllowedTables,
  getExportTableColumns,
  exportTableCSV,
} from "../../apiExport";

import api from "../../apiWithAccessToken";

const TYPE_BADGE = {
  numeric:     { label: "NUM",  bg: "rgba(56,178,172,0.12)",  color: "#38b2ac" },
  categorical: { label: "CAT",  bg: "rgba(129,140,248,0.12)", color: "#818cf8" },
  date:        { label: "DATE", bg: "rgba(251,191,36,0.12)",  color: "#f59e0b" },
};

// ─── Responsive breakpoint hook ───────────────────────────────────────────────
function useBreakpoint() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return {
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
    width,
  };
}

function TypeBadge({ filterType }) {
  const badge = TYPE_BADGE[filterType] || { label: "TXT", bg: "rgba(156,163,175,0.15)", color: "#9ca3af" };
  return (
    <span
      className="text-xs px-1.5 py-0.5 rounded font-mono font-medium tracking-wide shrink-0"
      style={{ backgroundColor: badge.bg, color: badge.color }}
    >
      {badge.label}
    </span>
  );
}

// ─── Numeric Filter ────────────────────────────────────────────────────────────
function NumericFilterPanel({ colName, currentFilter, onUpdate }) {
  const operator = currentFilter?.operator || "eq";
  const isBetween = operator === "between";

  const handleOperatorChange = (e) => {
    const newOp = e.target.value;
    if (newOp === "between") {
      onUpdate({ column: colName, type: "numeric", operator: "between", value: [currentFilter?.value ?? "", ""] });
    } else {
      const prevVal = Array.isArray(currentFilter?.value) ? currentFilter.value[0] : currentFilter?.value;
      onUpdate({ column: colName, type: "numeric", operator: newOp, value: prevVal !== "" && prevVal != null ? Number(prevVal) : undefined });
    }
  };

  const handleSingleValue = (e) => {
    const val = e.target.value;
    onUpdate({ column: colName, type: "numeric", operator, value: val === "" ? undefined : Number(val) });
  };

  const handleBetweenValue = (index, e) => {
    const val = e.target.value;
    const prev = Array.isArray(currentFilter?.value) ? [...currentFilter.value] : ["", ""];
    prev[index] = val === "" ? "" : Number(val);
    onUpdate({ column: colName, type: "numeric", operator: "between", value: prev });
  };

  return (
    <>
      <select
        className="w-full mb-1.5 px-2 rounded"
        style={{
          fontSize: 11, padding: "3px 6px",
          border: "1px solid var(--border)",
          backgroundColor: "var(--background)",
          color: "var(--foreground)",
        }}
        value={operator}
        onChange={handleOperatorChange}
      >
        <option value="eq">Equals</option>
        <option value="neq">Not equals</option>
        <option value="gt">Greater than</option>
        <option value="gte">Greater or equal</option>
        <option value="lt">Less than</option>
        <option value="lte">Less or equal</option>
        <option value="between">Between (range)</option>
      </select>
      {isBetween ? (
        <div className="flex items-center gap-1">
          <input type="number" placeholder="Min"
            className="w-full rounded"
            style={{
              fontSize: 11, padding: "3px 6px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              color: "var(--foreground)",
            }}
            value={Array.isArray(currentFilter?.value) ? currentFilter.value[0] ?? "" : ""}
            onChange={(e) => handleBetweenValue(0, e)} />
          <span style={{ fontSize: 11, color: "var(--muted-foreground)", flexShrink: 0 }}>–</span>
          <input type="number" placeholder="Max"
            className="w-full rounded"
            style={{
              fontSize: 11, padding: "3px 6px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              color: "var(--foreground)",
            }}
            value={Array.isArray(currentFilter?.value) ? currentFilter.value[1] ?? "" : ""}
            onChange={(e) => handleBetweenValue(1, e)} />
        </div>
      ) : (
        <input type="number"
          className="w-full rounded"
          style={{
            fontSize: 11, padding: "3px 6px",
            border: "1px solid var(--border)",
            backgroundColor: "var(--background)",
            color: "var(--foreground)",
          }}
          value={!Array.isArray(currentFilter?.value) ? currentFilter?.value ?? "" : ""}
          onChange={handleSingleValue} />
      )}
    </>
  );
}

// ─── Categorical Filter ────────────────────────────────────────────────────────
// Has inline search bar when options > 8
function CategoricalFilterPanel({ col, currentFilter, onUpdate }) {
  const operator = currentFilter?.operator || "in";
  const selectedValues = currentFilter?.values || [];
  const [optSearch, setOptSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!optSearch.trim()) return col.options || [];
    return (col.options || []).filter(o =>
      String(o).toLowerCase().includes(optSearch.toLowerCase())
    );
  }, [col.options, optSearch]);

  const showSearch = (col.options?.length || 0) > 8;

  const handleOperatorChange = (op) => {
    onUpdate({ column: col.fully_qualified_name, type: "categorical", operator: op, values: selectedValues });
  };

  const handleCheckbox = (opt, checked) => {
    const newValues = checked
      ? [...selectedValues, opt]
      : selectedValues.filter((v) => v !== opt);
    onUpdate(newValues.length
      ? { column: col.fully_qualified_name, type: "categorical", operator, values: newValues }
      : null
    );
  };

  return (
    <>
      {/* Operator toggle — compact */}
      <div className="flex items-center gap-0.5 mb-1.5 p-0.5 rounded"
        style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}>
        {["in", "not_in"].map((op) => (
          <button
            key={op}
            onClick={() => handleOperatorChange(op)}
            className="flex-1 rounded transition font-medium"
            style={{
              fontSize: 10,
              padding: "2px 0",
              backgroundColor: operator === op ? "var(--primary)" : "transparent",
              color: operator === op ? "var(--primary-foreground)" : "var(--muted-foreground)",
            }}
          >
            {op === "in" ? "Include" : "Exclude"}
          </button>
        ))}
      </div>

      {/* Search — only shown when list is long */}
      {showSearch && (
        <div className="relative mb-1">
          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 pointer-events-none"
            style={{ color: "var(--muted-foreground)" }} />
          <input
            type="text"
            placeholder="Search values..."
            value={optSearch}
            onChange={e => setOptSearch(e.target.value)}
            className="w-full pr-5 rounded"
            style={{
              paddingLeft: 20, paddingTop: 3, paddingBottom: 3,
              fontSize: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              color: "var(--foreground)",
              outline: "none",
            }}
          />
          {optSearch && (
            <button onClick={() => setOptSearch("")}
              className="absolute right-1 top-1/2 -translate-y-1/2">
              <X className="w-2.5 h-2.5" style={{ color: "var(--muted-foreground)" }} />
            </button>
          )}
        </div>
      )}

      {/* Options list — capped shorter */}
      <div style={{ maxHeight: 108, overflowY: "auto" }} className="pr-0.5">
        {filteredOptions.length > 0 ? filteredOptions.map((opt) => {
          const checked = selectedValues.includes(opt);
          return (
            <div
              key={opt}
              className="flex items-center gap-1.5 px-0.5 cursor-pointer rounded hover:opacity-80"
              style={{ color: "var(--foreground)", paddingTop: 2, paddingBottom: 2 }}
              onClick={() => handleCheckbox(opt, !checked)}
            >
              <input
                type="checkbox"
                readOnly
                style={{ accentColor: "var(--primary)", width: 11, height: 11, cursor: "pointer", pointerEvents: "none" }}
                checked={checked}
              />
              <span className="truncate" style={{ fontSize: 11 }}>{opt}</span>
            </div>
          );
        }) : (
          <p className="py-1.5 text-center" style={{ fontSize: 10, color: "var(--muted-foreground)" }}>No matches</p>
        )}
      </div>

      {selectedValues.length > 0 && (
        <p className="mt-1" style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
          {selectedValues.length} value{selectedValues.length !== 1 ? "s" : ""} selected
        </p>
      )}
    </>
  );
}

// ─── Date Filter Panel (Year/Month/Day Hierarchy) ─────────────────────────────
function DateFilterPanel({ options, selections, onToggleYear, onToggleMonth, onToggleDay, column }) {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  if (!options || options.length === 0) {
    return <p className="py-2 text-center text-xs" style={{ color: "var(--muted-foreground)" }}>No date data available</p>;
  }

  return (
    <div style={{ maxHeight: 220, overflowY: "auto" }} className="pr-1 select-none custom-scrollbar">
      {options.map((optYear) => {
        const selYear = selections.find(s => s.year === optYear.year);
        const isYearSelected = !!selYear;
        const selectedMonths = selYear?.months || [];
        const isYearExpanded = expanded[`y-${optYear.year}`];
        
        // Defensive check: fallback to empty array if months is undefined
        const monthsList = optYear.months || [];

        return (
          <div key={optYear.year} className="mb-1">
            {/* Year Row */}
            <div className="flex items-center gap-1.5 p-1 rounded cursor-pointer hover:opacity-80 transition-colors" 
                 style={{ color: "var(--foreground)" }}>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (monthsList.length > 0) toggleExpand(`y-${optYear.year}`); 
                }}
                className="p-0.5 rounded transition-colors"
                style={{ 
                  backgroundColor: isYearExpanded ? "var(--active-bg)" : "transparent",
                  opacity: monthsList.length ? 1 : 0, 
                  pointerEvents: monthsList.length ? 'auto' : 'none' 
                }}
              >
                <ChevronRight className={`w-3 h-3 transition-transform ${isYearExpanded ? "rotate-90" : ""}`} />
              </button>
              <div className="flex flex-1 items-center gap-2" onClick={() => onToggleYear(column, optYear.year)}>
                <input type="checkbox" readOnly checked={isYearSelected} className="w-3 h-3 pointer-events-none" style={{ accentColor: "var(--primary)" }} />
                <span className="text-xs font-semibold">{optYear.year}</span>
              </div>
            </div>

            {/* Months List */}
            {isYearExpanded && monthsList.length > 0 && (
              <div className="ml-5 flex flex-col mt-0.5" style={{ borderLeft: "1px solid var(--border)" }}>
                {monthsList.map(optMonth => {
                  const selMonth = selectedMonths.find(m => m.month === optMonth.month);
                  const isMonthSelected = !!selMonth;
                  const selectedDays = selMonth?.days || [];
                  const isMonthExpanded = expanded[`m-${optYear.year}-${optMonth.month}`];
                  
                  // Defensive check: fallback to empty array if days is undefined
                  const daysList = optMonth.days || [];

                  return (
                    <div key={optMonth.month} className="mb-0.5">
                       {/* Month Row */}
                       <div className="flex items-center gap-1.5 p-1 rounded cursor-pointer hover:opacity-80 transition-colors" style={{ color: "var(--foreground)" }}>
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (daysList.length > 0) toggleExpand(`m-${optYear.year}-${optMonth.month}`); 
                            }}
                            className="p-0.5 rounded transition-colors"
                            style={{ 
                              backgroundColor: isMonthExpanded ? "var(--active-bg)" : "transparent",
                              opacity: daysList.length ? 1 : 0, 
                              pointerEvents: daysList.length ? 'auto' : 'none' 
                            }}
                          >
                            <ChevronRight className={`w-3 h-3 transition-transform ${isMonthExpanded ? "rotate-90" : ""}`} />
                          </button>
                          <div className="flex flex-1 items-center gap-2" onClick={() => onToggleMonth(column, optYear.year, optMonth.month)}>
                            <input type="checkbox" readOnly checked={isMonthSelected} className="w-3 h-3 pointer-events-none" style={{ accentColor: "var(--primary)" }} />
                            <span className="text-xs">{monthNames[optMonth.month - 1] || `Month ${optMonth.month}`}</span>
                          </div>
                       </div>

                       {/* Days List */}
                       {isMonthExpanded && daysList.length > 0 && (
                         <div className="ml-5 flex flex-col gap-0.5 mt-0.5" style={{ borderLeft: "1px solid var(--border)" }}>
                           {daysList.map(day => {
                              const isDaySelected = selectedDays.includes(day);
                              return (
                                <div 
                                  key={day}
                                  onClick={() => onToggleDay(column, optYear.year, optMonth.month, day)}
                                  className="flex items-center gap-2 p-1 pl-2 rounded cursor-pointer hover:opacity-80"
                                >
                                  <input type="checkbox" readOnly checked={isDaySelected} className="w-3 h-3 pointer-events-none" style={{ accentColor: "var(--primary)" }} />
                                  <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>{day}</span>
                                </div>
                              );
                           })}
                         </div>
                       )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Filter Popover rendered via portal ────────────────────────────────────────
// Measures the trigger button's screen position and floats the popover above
// everything — never squeezed by the panel it lives in.
function FilterPortal({ anchorRef, col, filters, onApplyFilter, onClearFilter, onClose }) {
  const colName = col.fully_qualified_name;
  const [pos, setPos] = useState(null);
  const popoverRef = useRef(null);

  // --- DRAFT STATE ---
  const [draftFilter, setDraftFilter] = useState(() => filters[colName] || null);

  // --- DATE MUTATION LOGIC ---
  const handleToggleYear = (column, year) => {
    const current = draftFilter?.type === "date" ? (draftFilter.values || []) : [];
    const exists = current.find(v => v.year === year);
    const updated = exists ? current.filter(v => v.year !== year) : [...current, { year, months: [] }];
    setDraftFilter(updated.length ? { column, type: "date", values: updated } : null);
  };

  const handleToggleMonth = (column, year, month) => {
    const current = draftFilter?.type === "date" ? (draftFilter.values || []) : [];
    let yearNode = current.find(v => v.year === year);
    let updated;
    if (!yearNode) {
      updated = [...current, { year, months: [{ month, days: [] }] }];
    } else {
      updated = current.map(y => {
        if (y.year !== year) return y;
        const months = y.months || [];
        const monthExists = months.find(m => m.month === month);
        const newMonths = monthExists ? months.filter(m => m.month !== month) : [...months, { month, days: [] }];
        return { ...y, months: newMonths };
      });
    }
    setDraftFilter(updated.length ? { column, type: "date", values: updated } : null);
  };

  const handleToggleDay = (column, year, month, day) => {
    const current = draftFilter?.type === "date" ? (draftFilter.values || []) : [];
    let yearNode = current.find(v => v.year === year);
    let updated;
    if (!yearNode) {
      updated = [...current, { year, months: [{ month, days: [day] }] }];
    } else {
      updated = current.map(y => {
        if (y.year !== year) return y;
        const months = y.months || [];
        let monthNode = months.find(m => m.month === month);
        let newMonths;
        if (!monthNode) {
          newMonths = [...months, { month, days: [day] }];
        } else {
          newMonths = months.map(m => {
            if (m.month !== month) return m;
            const days = m.days || [];
            const newDays = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
            return { ...m, days: newDays };
          });
        }
        return { ...y, months: newMonths };
      });
    }
    setDraftFilter(updated.length ? { column, type: "date", values: updated } : null);
  };

  // --- INCOMPLETE CHECK ---
  const isDraftIncomplete = draftFilter?.type === "numeric" && draftFilter?.operator === "between" && 
    !(Array.isArray(draftFilter?.value) && draftFilter.value[0] !== "" && draftFilter.value[0] != null && draftFilter.value[1] !== "" && draftFilter.value[1] != null);

  // Measure anchor position on mount and window resize only
  useEffect(() => {
    const measure = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const PW = Math.min(272, window.innerWidth - 16);
      const POPOVER_HEIGHT = 260; // Slightly taller for the Apply button
      const MARGIN = 8;

      let left = r.left;
      if (left + PW + MARGIN > window.innerWidth) left = r.right - PW;
      left = Math.max(MARGIN, left);

      const spaceBelow = window.innerHeight - r.bottom - MARGIN;
      const spaceAbove = r.top - MARGIN;
      let top;
      let openUpward = false;
      if (spaceBelow >= POPOVER_HEIGHT || spaceBelow >= spaceAbove) {
        top = r.bottom + 6;
      } else {
        openUpward = true;
        top = r.top - 6;
      }
      setPos({ top, left, openUpward, width: PW });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [anchorRef]);

  // Close when anything OUTSIDE the popover scrolls
  useEffect(() => {
    const handleScroll = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [onClose]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  if (!pos) return null;

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={{
        position: "fixed", top: pos.top, left: pos.left,
        transform: pos.openUpward ? "translateY(-100%)" : "none",
        width: pos.width, zIndex: 9999,
        backgroundColor: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 10, padding: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="truncate font-semibold" style={{ fontSize: 11, color: "var(--foreground)", maxWidth: 150 }}>
          {col.name}
        </span>
        <div className="flex items-center gap-1">
          <TypeBadge filterType={col.filter_type} />
          <button onClick={onClose} className="p-0.5 rounded hover:opacity-60 transition ml-0.5">
            <X className="w-2.5 h-2.5" style={{ color: "var(--muted-foreground)" }} />
          </button>
        </div>
      </div>

      {col.filter_type === "categorical" && (
        <CategoricalFilterPanel col={col} currentFilter={draftFilter} onUpdate={setDraftFilter} />
      )}
      {col.filter_type === "numeric" && (
        <NumericFilterPanel colName={colName} currentFilter={draftFilter} onUpdate={setDraftFilter} />
      )}
      {col.filter_type === "date" && (
        <DateFilterPanel
          column={colName}
          options={col.options}
          selections={draftFilter?.type === "date" ? (draftFilter.values || []) : []}
          onToggleYear={handleToggleYear}
          onToggleMonth={handleToggleMonth}
          onToggleDay={handleToggleDay}
        />
      )}
        
      {isDraftIncomplete && (
        <p className="mt-1 leading-snug" style={{ fontSize: 10, color: "#f59e0b" }}>
          Enter both min and max values.
        </p>
      )}

      {/* Footer with Apply and Clear */}
      <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
        <button
          className="flex-1 rounded transition hover:opacity-80 py-1.5 font-medium"
          style={{ fontSize: 11, color: "var(--muted-foreground)", border: "1px solid var(--border)" }}
          onClick={() => { onClearFilter(colName); onClose(); }}
        >
          Clear
        </button>
        <button
          className="flex-1 rounded font-medium transition hover:opacity-90 py-1.5"
          style={{ fontSize: 11, backgroundColor: "var(--primary)", color: "var(--primary-foreground)", opacity: isDraftIncomplete ? 0.5 : 1 }}
          disabled={isDraftIncomplete}
          onClick={() => { onApplyFilter(colName, draftFilter); onClose(); }}
        >
          Apply Filter
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─── Column row with filter button ────────────────────────────────────────────
function ColumnRow({ col, isChecked, isLast, filters, openFilter, onToggleColumn, onSetOpenFilter, onApplyFilter, onClearFilter, isFilterActive, hasIncompleteBetween, isMobile }) {
  const btnRef = useRef(null);
  const filterActive = isFilterActive(col.fully_qualified_name);
  const filterIncomplete = hasIncompleteBetween(col.fully_qualified_name);
  const canFilter = col.can_filter !== false;
  const isOpen = openFilter === col.fully_qualified_name;

  return (
    <div
      className="flex items-center justify-between transition-colors"
      style={{
        padding: isMobile ? "8px 10px" : "10px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--border)",
        backgroundColor: isChecked ? "var(--active-bg)" : "transparent",
      }}
    >
      <div className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer" onClick={() => onToggleColumn(col)}>
        <input
          type="checkbox" readOnly className="cursor-pointer shrink-0"
          style={{ accentColor: "var(--primary)", pointerEvents: "none" }}
          checked={isChecked}
        />
        <span
          className="text-xs truncate"
          style={{ color: isChecked ? "var(--primary)" : "var(--foreground)", fontWeight: isChecked ? 500 : 400, fontFamily: "monospace" }}
        >
          {col.name}
        </span>
        <TypeBadge filterType={col.filter_type} />
      </div>

      {isChecked && (
        <>
          <button
            ref={btnRef}
            onClick={() => {
              if (!canFilter) {
                onSetOpenFilter(null);
                return;
              }
              onSetOpenFilter(isOpen ? null : col.fully_qualified_name);
            }}
            disabled={!canFilter}
            title={!canFilter ? "Filtering is disabled for masked columns" : "Filter column"}
            className={`shrink-0 ml-1 p-1 transition relative ${canFilter ? "hover:opacity-80" : "opacity-45 cursor-not-allowed"}`}
            style={{ backgroundColor: "transparent" }}
          >
            <Filter 
              className="w-3 h-3" 
              fill={canFilter && filterActive ? "currentColor" : "none"}
              style={{ color: !canFilter ? "var(--muted-foreground)" : filterIncomplete ? "#f59e0b" : filterActive ? "var(--primary)" : "var(--muted-foreground)" }} 
            />
            {canFilter && filterIncomplete && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#f59e0b" }} />}
          </button>

          {isOpen && canFilter && (
            <FilterPortal
              anchorRef={btnRef}
              col={col}
              filters={filters}
              onApplyFilter={onApplyFilter}
              onClearFilter={onClearFilter}
              onClose={() => onSetOpenFilter(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Single Table Column Panel ─────────────────────────────────────────────────
function TableColumnPanel({
  tableName, cols, isBase, selectedColumns, filters, openFilter,
  onToggleColumn, onToggleGroup, onSetOpenFilter, onApplyFilter, onClearFilter,
  isFilterActive, hasIncompleteBetween, isMobile, isTablet,
}) {
  const names = cols.map(c => c.fully_qualified_name);
  const selectedCount = names.filter(n => selectedColumns.includes(n)).length;
  const groupState = selectedCount === 0 ? "none" : selectedCount === names.length ? "all" : "partial";
  const isSmall = isMobile || isTablet;

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div
        className="flex items-start justify-between shrink-0 gap-2"
        style={{
          padding: isSmall ? "8px 10px" : "10px 16px",
          background: isBase ? "var(--neon-gradient, linear-gradient(135deg, #3b82f6 0%, #d946ef 100%))" : "var(--surface)",
          border: "1px solid var(--border)", borderBottom: "none", borderRadius: "12px 12px 0 0",
       }}
      >
        <div className="flex items-start gap-1.5 flex-1">
          <Database className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "var(--primary-foreground)" }} />
          <span className="text-xs font-bold tracking-wide uppercase leading-tight" style={{ color: "var(--primary-foreground)", wordBreak: "break-word", whiteSpace: "normal" }}>
            {tableName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2 mt-px">
          <span className="text-xs font-medium" style={{ color: isBase ? "rgba(255,255,255,0.75)" : "var(--muted-foreground)" }}>
            {selectedCount}/{cols.length}
          </span>
          <button
            onClick={() => onToggleGroup(cols, groupState)}
            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded transition font-bold"
            style={{
              border: `1px solid ${isBase ? "rgba(255,255,255,0.3)" : "var(--border)"}`,
              backgroundColor: groupState !== "none" ? isBase ? "rgba(255,255,255,0.2)" : "var(--active-bg)" : "transparent",
              color: isBase ? "var(--primary-foreground)" : groupState !== "none" ? "var(--primary)" : "var(--muted-foreground)",
            }}
          >
            {groupState === "all" ? "Deselect" : "Select all"}
          </button>
        </div>
      </div>
      <div
        className="flex-1 overflow-y-auto no-scrollbar"
        style={{ border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", backgroundColor: "var(--surface)" }}
      >
        {cols.map((col, idx) => (
          <ColumnRow
            key={col.fully_qualified_name}
            col={col}
            isChecked={selectedColumns.includes(col.fully_qualified_name)}
            isLast={idx === cols.length - 1}
            filters={filters}
            openFilter={openFilter}
            onToggleColumn={onToggleColumn}
            onSetOpenFilter={onSetOpenFilter}
            onApplyFilter={onApplyFilter}
            onClearFilter={onClearFilter}
            isFilterActive={isFilterActive}
            hasIncompleteBetween={hasIncompleteBetween}
            isMobile={isMobile}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function DataDumpPage() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [openTableDropdown, setOpenTableDropdown] = useState(false);

  const { isMobile, isTablet } = useBreakpoint();
  const [leftPanelOpen, setLeftPanelOpen] = useState(false); 

  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState("");

  const filterRef = useRef(null);
  const tableRef = useRef(null);

  const TABLE_DROPDOWN_LIMIT = 5;

  const filteredTables = useMemo(() => {
    const all = tables.filter(t => t.toLowerCase().includes(tableSearch.toLowerCase()));
    return { results: all.slice(0, TABLE_DROPDOWN_LIMIT), total: all.length };
  }, [tables, tableSearch]);

  const groupedColumns = useMemo(() => {
    if (!selectedTable || !columns.length) return {};
    return { [selectedTable]: columns };
  }, [columns, selectedTable]);

  /* 1. FETCH DATABASES ON MOUNT */
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get("/auth/me");
        const data = response.data; 
        
        if (data?.databases?.length > 0) {
          setDatabases(data.databases);
          setSelectedDb(data.databases[0].id); 
        }
      } catch (error) { 
        console.error("DB Fetch Error:", error);
      }
    })();
  }, []);

 /* 2. LOAD TABLES WHEN DATABASE CHANGES */
  useEffect(() => {
    if (!selectedDb) return;
    (async () => {
      try {
        const { data } = await getAllowedTables(selectedDb);
        setTables(data.allowed_tables || []);
        setSelectedTable("");
        setColumns([]);
        setSelectedColumns([]);
        setFilters({});
      } catch { toast.error("Failed to load tables"); }
    })();
  }, [selectedDb]);

  /* 3. FETCH COLUMNS WHEN TABLE CHANGES */
  useEffect(() => {
    if (!selectedTable || !selectedDb) return;
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getExportTableColumns(selectedTable, selectedDb);
        if (!isMounted) return;

        const merged = data.tables_metadata?.[selectedTable] || [];
        setColumns(merged);
        const validNames = new Set(merged.map(c => c.fully_qualified_name));
        const filterableNames = new Set(
          merged.filter(c => c.can_filter !== false).map(c => c.fully_qualified_name)
        );
        setSelectedColumns(prev => prev.filter(c => validNames.has(c)));
        setFilters(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(k => {
            if (!validNames.has(k) || !filterableNames.has(k)) delete next[k];
          });
          return next;
        });
        setOpenFilter(prev => (prev && !filterableNames.has(prev) ? null : prev));
      } catch { 
        if (isMounted) toast.error("Failed to load column metadata"); 
      } finally { 
        if (isMounted) setLoading(false); 
      }
    })();
    return () => { isMounted = false; };
  }, [selectedTable, selectedDb]);

  const filterCount = useMemo(() => {
    return Object.values(filters).filter(f => {
      if (f.type === "date") return f.values?.some(v => v.months?.length > 0);
      if (f.type === "numeric" && f.operator === "between") {
        return Array.isArray(f.value) && f.value[0] !== "" && f.value[0] != null && f.value[1] !== "" && f.value[1] != null;
      }
      return true;
    }).length;
  }, [filters]);

  useEffect(() => {
    const h = (e) => { if (tableRef.current && !tableRef.current.contains(e.target)) setOpenTableDropdown(false); };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, []);

  const toggleColumn = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col.fully_qualified_name)
        ? prev.filter(c => c !== col.fully_qualified_name)
        : [...prev, col.fully_qualified_name]
    );
  };

  const toggleGroupColumns = (cols, state) => {
    const names = cols.map(c => c.fully_qualified_name);
    if (state === "all") {
      setSelectedColumns(prev => prev.filter(c => !names.includes(c)));
    } else {
      setSelectedColumns(prev => [...new Set([...prev, ...names])]);
    }
  };

  const updateFilter = (columnName, filterObj) => {
    const colMeta = columns.find(c => c.fully_qualified_name === columnName);
    if (colMeta && colMeta.can_filter === false) return;
    setFilters(prev => {
      if (!filterObj) { const next = { ...prev }; delete next[columnName]; return next; }
      return { ...prev, [columnName]: filterObj };
    });
  };

  const handleExport = async () => {
    if (!selectedTable) return toast.warning("Select a table");
    if (!selectedColumns.length) return toast.warning("Select at least one column");

    const filterableColumnNames = new Set(
      columns.filter(c => c.can_filter !== false).map(c => c.fully_qualified_name)
    );
    const payload = {
      db_id: selectedDb,
      table_name: selectedTable,
      columns: selectedColumns,
      filters: Object.values(filters).filter(f => {
        if (!filterableColumnNames.has(f.column)) return false;
        if (f.type === "date") return f.values?.some(v => v.months?.length > 0);
        if (f.type === "numeric" && f.operator === "between") {
          return Array.isArray(f.value) && f.value[0] !== "" && f.value[0] != null && f.value[1] !== "" && f.value[1] != null;
        }
        return true;
      }),
    };

    try {
      setExporting(true);
      const { data } = await exportTableCSV(payload);
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedTable}_export.csv`;
      link.click();
      toast.success("Export successful");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const hasIncompleteBetween = (colName) => {
    const f = filters[colName];
    if (!f || f.type !== "numeric" || f.operator !== "between") return false;
    return !(Array.isArray(f.value) && f.value[0] !== "" && f.value[0] != null && f.value[1] !== "" && f.value[1] != null);
  };

  const isFilterActive = (colName) => {
    const f = filters[colName];
    if (!f) return false;
    if (f.type === "date") return f.values && f.values.length > 0;
    if (f.type === "numeric" && f.operator === "between") {
      return Array.isArray(f.value) && f.value[0] !== "" && f.value[0] != null && f.value[1] !== "" && f.value[1] != null;
    }
    return true;
  };

  const tableColumnPanelProps = {
    selectedColumns, 
    filters, 
    openFilter,
    onToggleColumn: toggleColumn,
    onToggleGroup: toggleGroupColumns,
    onSetOpenFilter: setOpenFilter,
    onApplyFilter: updateFilter,
    onClearFilter: (colName) => updateFilter(colName, null),
    isFilterActive,
    hasIncompleteBetween,
    isMobile,
    isTablet,
  };

  return (
    <div className="flex flex-col h-full"
      style={{ backgroundColor: "var(--background)", color: "var(--foreground)", fontFamily: "inherit" }}>

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-3 md:px-6 py-3 shrink-0"
        style={{ backgroundColor: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <Database className="w-5 h-5 shrink-0" style={{ color: "var(--primary)" }} />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate" style={{ color: "var(--foreground)" }}>Data Export</h1>
            {!isMobile && (
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Select tables, columns, and filters to export
              </p>
            )}
          </div>
        </div>

        {/* --- Database Selector & Buttons --- */}
        <div className="flex items-center gap-4 shrink-0">
          
          {/* Connected To Dropdown */}
          {!isMobile && (
            <div className="flex items-center gap-2 mr-2">
              <span className="flex items-center gap-1.5 text-sm" style={{ color: "var(--muted-foreground)" }}>
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Connected to:
              </span>
              {databases.length > 0 ? (
                <select
                  value={selectedDb}
                  onChange={(e) => {
                  setSelectedDb(Number(e.target.value));
                  setSelectedTable(""); 
                  setColumns([]);
                  setSelectedColumns([]);
                  setFilters({});
                }}
                  className="text-sm border rounded-lg px-2.5 py-1.5 outline-none font-medium cursor-pointer transition-colors"
                  style={{ 
                    backgroundColor: "var(--background)", 
                    borderColor: "var(--border)", 
                    color: "var(--foreground)" 
                  }}
                >
                  {databases.map((db) => (
                    <option key={db.id} value={db.id}>{db.display_label}</option>
                  ))}
                </select>
              ) : (
                <div className="text-sm border rounded-lg px-2.5 py-1.5" style={{ backgroundColor: "var(--active-bg)", borderColor: "var(--border)", color: "var(--muted-foreground)" }}>
                  Loading DBs...
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 md:gap-2">
            
            {/* Filter Badge */}
            {filterCount > 0 && (
              <div className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-sm font-medium"
                style={{ backgroundColor: "var(--active-bg)", color: "var(--primary)", border: "1px solid var(--border)" }}>
                <Filter className="w-3.5 h-3.5" />
                {!isMobile && "Filters"}
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs"
                  style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}>
                  {filterCount}
                </span>
              </div>
            )}

            {/* Export CSV Button */}
            <button
              onClick={handleExport}
              disabled={exporting || !selectedTable || selectedColumns.length === 0}
              className="flex items-center gap-2 px-3 md:px-4 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 hover:opacity-90"
              style={{ 
                background: "var(--neon-gradient, linear-gradient(135deg, #3b82f6 0%, #d946ef 100%))", 
                color: "#ffffff",
                border: "none",
                boxShadow: "0 4px 14px rgba(217, 70, 239, 0.25)"
              }}
            >
              {exporting ? (
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Download className="w-4 h-4 text-white" />
              )}
              {!isMobile && "Export CSV"}
            </button>
            
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex flex-1 overflow-hidden min-h-0" style={{ gap: isMobile ? 0 : 12, padding: isMobile ? 8 : isTablet ? 12 : 16 }}>

        {/* ── LEFT PANEL ──
            Desktop/Tablet : always visible sidebar
            Mobile         : slide-in drawer overlay                          */}
        {isMobile && leftPanelOpen && (
          <div
            className="fixed inset-0 z-40"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
            onClick={() => setLeftPanelOpen(false)}
          />
        )}
        <div
          className="flex flex-col gap-3 shrink-0"
          style={{
            width: isMobile ? 240 : isTablet ? 200 : 210,
            overflowY: "auto",
            // Mobile: fixed overlay drawer sliding in from left
            ...(isMobile ? {
              position: "fixed",
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 50,
              transform: leftPanelOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.22s ease",
              backgroundColor: "var(--background)",
              padding: 12,
              boxShadow: "4px 0 24px rgba(0,0,0,0.3)",
            } : {}),
          }}
        >
          {/* Mobile close button */}
          {isMobile && (
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold" style={{ color: "var(--foreground)" }}>Configure Export</span>
              <button onClick={() => setLeftPanelOpen(false)}>
                <X className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
              </button>
            </div>
          )}

          {/* Source Table */}
          <div className="rounded-xl p-3 md:p-4 flex flex-col min-h-0" style={{ backgroundColor: isMobile ? "var(--background)" : "var(--surface)", border: "1px solid var(--border)", maxHeight: "400px" }}>
            <div className="flex items-center gap-2 mb-3 shrink-0">
              <TableProperties className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--primary)" }} />
              <span className="text-xs font-semibold" style={{ color: "var(--foreground)" }}>Source Table</span>
            </div>
            
            <div className="flex flex-col min-h-0 flex-1">
              {/* Strictly a Search Input */}
              <div className="relative shrink-0 mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                  style={{ color: "var(--muted-foreground)" }} />
                <input
                  type="text"
                  value={tableSearch} 
                  placeholder="Search tables..."
                  onChange={(e) => setTableSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-md text-sm transition-colors"
                  style={{ border: "1px solid var(--border)", backgroundColor: "var(--background)", color: "var(--foreground)", outline: "none" }}
                  onFocus={(e) => e.target.style.borderColor = "var(--primary)"}
                  onBlur={(e) => e.target.style.borderColor = "var(--border)"}
                />
              </div>
              
              {/* Always-visible Scrollable List */}
              {tables.length > 0 && (
                <div className="rounded-md overflow-y-auto custom-scrollbar flex-1"
                  style={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)" }}>
                  {filteredTables.results.length > 0 ? (
                    <>
                      {filteredTables.results.map(table => {
                        const isSelected = selectedTable === table;
                        return (
                          <div
                            key={table}
                            onClick={() => { setSelectedTable(table); setColumns([]); setFilters({}); if (isMobile) setLeftPanelOpen(false); }}
                            className="px-3 py-2 text-xs cursor-pointer transition-colors border-b last:border-0"
                            style={{ 
                              borderColor: "var(--border)",
                              color: isSelected ? "var(--primary)" : "var(--foreground)", 
                              backgroundColor: isSelected ? "var(--active-bg)" : "transparent",
                              fontWeight: isSelected ? 600 : 400
                            }}
                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = "var(--active-bg)"; }}
                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = "transparent"; }}
                          >
                            {table}
                          </div>
                        )
                      })}
                      {filteredTables.total > TABLE_DROPDOWN_LIMIT && (
                        <div className="px-3 py-2 text-xs text-center cursor-pointer transition-colors hover:underline"
                          style={{ color: "var(--primary)", backgroundColor: "var(--background)" }}
                          onClick={() => setTableSearch("")}
                        >
                          +{filteredTables.total - TABLE_DROPDOWN_LIMIT} more...
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-3 text-xs text-center" style={{ color: "var(--muted-foreground)" }}>No tables found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Mobile: show selected table + open drawer button when no table selected */}
          {isMobile && !selectedTable && (
            <button
              onClick={() => setLeftPanelOpen(true)}
              className="flex items-center justify-center gap-2 mb-2 py-2 rounded-xl text-sm font-medium shrink-0"
              style={{ backgroundColor: "var(--surface)", border: "1px dashed var(--border)", color: "var(--primary)" }}
            >
              <TableProperties className="w-4 h-4" />
              Tap to select a table
            </button>
          )}

          {loading ? (
            <div className="flex-1 flex items-center justify-center gap-2 rounded-xl"
              style={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)", color: "var(--muted-foreground)" }}>
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
              <span className="text-sm">Loading columns...</span>
            </div>
          ) : columns.length > 0 ? (
            <>
              {/* Toolbar row */}
              <div className="flex items-center justify-between mb-2 shrink-0 flex-wrap gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Columns className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--primary)" }} />
                  <span className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>Columns</span>
                </div>
                <div className="flex items-center gap-1.5 md:gap-2">
                  {/* Mobile: show table config button */}
                  {isMobile && (
                    <button
                      onClick={() => setLeftPanelOpen(true)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-md"
                      style={{ border: "1px solid var(--border)", color: "var(--primary)", backgroundColor: "var(--active-bg)" }}
                    >
                      <TableProperties className="w-3 h-3" />
                      {selectedTable || "Table"}
                    </button>
                  )}
                  
                  {/* The duplicate 'Select all' button has been completely removed from here */}
                  
                  {selectedColumns.length > 0 && (
                    <span className="text-xs px-2 md:px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "var(--active-bg)", color: "var(--primary)", border: "1px solid var(--border)" }}>
                      {selectedColumns.length} selected
                    </span>
                  )}
                </div>
              </div>

              {/* Side-by-side panels */}
              <div
                className="flex-1 min-h-0"
                style={{ display: "flex", gap: isMobile ? 8 : 12, overflowX: "auto", overflowY: "hidden", alignItems: "stretch" }}
              >
                {Object.entries(groupedColumns).map(([tableName, cols], idx) => (
                  <div
                    key={tableName}
                    style={{
                      flex: "1 1 0",
                      minWidth: isMobile ? 160 : 220,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                    }}
                  >
                    <TableColumnPanel
                      tableName={tableName}
                      cols={cols}
                      isBase={idx === 0}
                      {...tableColumnPanelProps}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 rounded-xl"
              style={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)" }}>
              <div className="p-4 rounded-xl" style={{ backgroundColor: "var(--active-bg)" }}>
                <Columns className="w-7 h-7" style={{ color: "var(--muted-foreground)" }} />
              </div>
              <p className="text-sm font-medium" style={{ color: "var(--foreground)" }}>No table selected</p>
              <p className="text-xs text-center max-w-[200px]" style={{ color: "var(--muted-foreground)" }}>
                {isMobile ? "Tap the table icon in the header to begin" : "Search and select a table from the left panel to view its columns"}
              </p>
              {isMobile && (
                <button
                  onClick={() => setLeftPanelOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
                >
                  <TableProperties className="w-4 h-4" />
                  Select a table
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <div className="py-2.5 text-center text-xs shrink-0"
        style={{ color: "var(--muted-foreground)", borderTop: "1px solid var(--border)" }}>
        © 2026 ScatterPie Analytics Pvt. Ltd. •{" "}
        <a href="https://www.scatterpie.io/privacy-policy/" style={{ color: "var(--primary)" }}>Privacy Policy</a>
      </div>
    </div>
  );
}
